import { createFileRoute, Link } from '@tanstack/react-router';
import env from '../../environment';
import {
  ArrowRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { basicHead, ogImage } from '../../utility/head';
import { getButtonStyles } from '@sc/shared-ui';
import LaunchShape from '../../assets/shapes/launch.svg?react';
import EmpowerShape from '../../assets/shapes/empower.svg?react';
import EyeOverlayIcon from '../../assets/shapes/eye-overlay.svg?react';
import ClockStopwatchIcon from '../../assets/icons/clock-stopwatch.svg?react';
import { LogoSlider } from '../../components/LogoSlider';
import { BottomCTA } from '../../components/BottomCTA';
import { FaqList } from '../../components/FaqList';
import { ResponsiveImage } from '../../components/ResponsiveImage';
import { CoverageDemo } from '../../components/CoverageDemo';
import { featurePages } from '../../assets/data/features';
import { USAGE_CREDIT_STEPS } from '../../assets/data/pricing';

const FAQS = [
  {
    q: 'What can Stark Crawl extract from the web?',
    a: 'Point Stark Crawl at directories, listings, and research targets. It discovers hosts, crawls pages, and extracts structured fields — company details, contacts, product data, and other prospect signals you define for market research.',
  },
  {
    q: 'Can I run separate crawl projects for different markets or segments?',
    a: 'Yes. Each project has its own targets, crawl rules, and capture schemas — keep markets, competitors, or prospect lists cleanly separated.',
  },
  {
    q: 'Can my whole research team use Stark Crawl?',
    a: 'Yes. Invite teammates to shared projects so discovery, crawling, and prospect capture stay in one place instead of scattered spreadsheets and one-off scrapers.',
  },
  {
    q: 'Is my crawl data safe and private?',
    a: 'Your projects and extracted data live in an isolated environment dedicated to your account, encrypted in transit and at rest. They are never shared with third parties and never used to train AI models.',
  },
  {
    q: 'Do I need engineers to run crawls?',
    a: 'No. Configure targets, schemas, and capture rules in the product — Stark Crawl handles discovery, crawling, and structured extraction without a custom scraping codebase.',
  },
  {
    q: 'Can I export structured data for analysis?',
    a: 'Yes. Captured prospects and extracted fields are research-ready so you can push them into spreadsheets, CRMs, or your own analytics workflows.',
  },
];

const IndexComponent = () => (
  <div className="relative overflow-x-hidden">
    <LaunchShape
      className="absolute left-30 top-40 -translate-x-1/2 w-160 opacity-4 pointer-events-none"
      aria-hidden="true"
    />
    <EmpowerShape
      className="absolute right-0 top-320 translate-x-1/2 w-216 opacity-4 pointer-events-none"
      aria-hidden="true"
    />

    {/* ─── Hero ─────────────────────────────────────────────────────────── */}
    <section className="pt-15 lg:pt-20 px-5">
      <div className="max-w-240 mx-auto text-center flex flex-col items-center gap-5">
        <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white">
          Web crawling and structured data for{' '}
          <span className="text-primary-400">market research</span>
        </h1>
        <p className="text-lg leading-6.5 text-grey-300">
          Discover hosts, crawl pages, extract structured fields, and capture
          prospects — turn directories and the open web into research-ready data
          without building your own scrapers.
        </p>
        <div className="flex items-center gap-3 flex-wrap justify-center py-5">
          <a
            href={env.appUrl + '/register'}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Try Stark Crawl for free (opens in new tab)"
            {...getButtonStyles({
              type: 'primary',
              size: 'xl',
              additionalClassnames: 'gap-x-2',
            })}
          >
            Try for free
            <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
          </a>
        </div>
      </div>
      <div className="max-w-262 mx-auto mt-10">
        <div className="relative flex flex-col items-center rounded-lg overflow-hidden border-8 border-grey-t-700">
          <CoverageDemo />
        </div>
      </div>
    </section>

    {/* ─── Trusted By ───────────────────────────────────────────────────── */}
    <section className="py-15 px-5">
      <div className="max-w-315 mx-auto text-center">
        <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-5">
          Trusted by
        </p>
        <LogoSlider>
          <img src="/logos/able.svg" alt="Able" className="max-h-12.5" />
          <img src="/logos/aduro.svg" alt="Aduro" className="max-h-12.5" />
          <img
            src="/logos/bsquared.webp"
            alt="BSQuared"
            className="max-h-12.5 opacity-30"
          />
          <img src="/logos/tfp.svg" alt="TFP" className="max-h-12.5" />
          <img src="/logos/unibeez.svg" alt="Unibeez" className="max-h-12.5" />
        </LogoSlider>
      </div>
    </section>

    {/* ─── Features ─────────────────────────────────────────────────────── */}
    <section className="pt-25 pb-10 px-5">
      <div className="max-w-315 mx-auto">
        <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
          Features
        </p>
        <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-10 text-center">
          Why use Stark Crawl?
        </h2>
        <p className="text-lg leading-6.5 mb-15 text-center text-grey-300">
          Custom{' '}
          <Link
            to="/web-crawling-for-research"
            className="text-primary-300 underline underline-offset-2 hover:text-primary-200"
          >
            web crawling and data extraction
          </Link>{' '}
          usually means brittle scrapers, proxy farms, and engineering time.
          Stark Crawl is built for market research teams — discover hosts, crawl
          pages, extract structured fields, and capture prospects in one
          workflow.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featurePages.map((f) => (
            <Link
              key={f.slug}
              to={`/${f.slug}`}
              className="bg-background-tertiary rounded-md backdrop-blur-[6.25rem] p-5 flex flex-col gap-5 flex-1 hover:bg-grey-t-900 transition-colors"
            >
              <div className="bg-primary-t-1000 inline-flex items-center justify-center p-4 rounded self-start shrink-0">
                <f.Icon
                  className="w-6 h-6 text-primary-400"
                  aria-hidden="true"
                />
              </div>
              <h3 className="font-grotesque text-[1.875rem] leading-9 font-semibold text-white">
                {f.navTitle}
              </h3>
              <p className="text-base leading-6 text-grey-200">
                {f.navDescription}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ─── Private & Secure ─────────────────────────────────────────────── */}
    <section className="pt-25 pb-10 px-5">
      <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10 items-center mb-25">
        <div className="flex flex-col gap-5 items-start w-full lg:w-115 order-2 lg:order-1">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
            Access controls
          </p>
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
            You choose who can access your crawl projects
          </h2>
          <p className="text-base leading-6 text-grey-200">
            Each project can have access controls that fit how your research team
            works.
          </p>
          <ul className="flex flex-col gap-5 items-start" role="list">
            <li className="flex gap-2 items-center">
              <LockOpenIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Public - available to anyone, controlled by rate limits
              </p>
            </li>
            <li className="flex gap-2 items-center">
              <UserGroupIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Domain controlled - @yourcompany.com with magic links
              </p>
            </li>
            <li className="flex gap-2 items-center">
              <EnvelopeIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Specific emails - only those who you invite
              </p>
            </li>
          </ul>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/privacy"
              {...getButtonStyles({
                type: 'basic',
                size: 'large',
              })}
            >
              Privacy policy
            </Link>
          </div>
        </div>
        <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
          <div className="relative flex flex-col items-center justify-center gap-8 w-full min-w-0">
            <div className="relative flex items-center justify-center w-full max-w-125 min-w-0">
              <EyeOverlayIcon
                className="z-40 w-full h-auto pointer-events-none"
                aria-hidden="true"
              />
              <ResponsiveImage
                src="/images/security.png"
                alt="Research team collaborating on crawl project access"
                className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
                sizes="400px"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Structured extraction ─────────────────────────────────────────── */}
      <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10 items-center">
        <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
          <div className="relative flex items-center justify-center w-125 max-w-full">
            <EyeOverlayIcon
              className="z-40 w-full h-auto pointer-events-none"
              aria-hidden="true"
            />

            <ResponsiveImage
              src="/images/theme.png"
              alt="Analyst reviewing structured research data and charts"
              className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
              sizes="400px"
              loading="lazy"
            />
          </div>
        </div>
        <div className="flex flex-col gap-5 items-start w-full lg:w-115 order-2">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
            Structured extraction
          </p>
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
            Capture the fields your research needs
          </h2>
          <p className="text-base leading-6 text-grey-200">
            Define the schema once — company details, contacts, product data,
            or any prospect signal — then Stark Crawl discovers hosts, crawls
            pages, and fills those fields consistently. Research-ready lists,
            not raw HTML dumps.
          </p>
          <ul className="flex flex-col gap-5 items-start" role="list">
            <li className="flex gap-2 items-center">
              <TableCellsIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Schema-defined fields
              </p>
            </li>
            <li className="flex gap-2 items-center">
              <MagnifyingGlassIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Host discovery & directory crawl
              </p>
            </li>
            <li className="flex gap-2 items-center">
              <DocumentTextIcon
                className="w-5 h-5 text-grey-400 shrink-0"
                aria-hidden="true"
              />
              <p className="font-open text-base leading-6 text-white">
                Research-ready prospect capture
              </p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    {/* ─── How It Works (Steps) ─────────────────────────────────────────── */}
    <section className="pt-25 pb-10 px-5">
      <div className="max-w-315 mx-auto">
        <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
          How it works
        </p>
        <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
          Get started in 10 minutes
        </h2>
        <ul className="flex flex-col lg:flex-row gap-y-10 gap-x-10" role="list">
          {[
            {
              n: '1',
              title: 'Define targets',
              body: 'Choose markets, directories, and host sources you want to cover for research.',
              time: '3 minutes',
            },
            {
              n: '2',
              title: 'Set the schema',
              body: 'Define the structured fields and prospect signals you need to extract.',
              time: '2 minutes',
            },
            {
              n: '3',
              title: 'Crawl & extract',
              body: 'Stark Crawl discovers hosts, crawls pages, and captures structured data against your rules.',
              time: '4 minutes',
            },
            {
              n: '4',
              title: 'Review & export',
              body: 'Share the project with your team, refine captures, and export research-ready lists.',
              time: '1 minute',
            },
          ].map(({ n, title, body, time }) => (
            <li key={n} className="relative">
              <h6
                className="font-grotesque text-[10rem] leading-25 -top-4 opacity-10 absolute font-bold text-primary-t-600 select-none block"
                aria-hidden="true"
              >
                {n}
              </h6>
              <h3 className="font-grotesque mt-18 text-[1.875rem] leading-9 font-semibold text-white relative">
                {title}
              </h3>
              <p className="text-base leading-6 text-grey-200 mt-5 mb-5">
                {body}
              </p>
              <div className="inline-flex items-center gap-2 bg-primary-1000 border border-primary-700 rounded-full px-4 py-2">
                <ClockStopwatchIcon
                  className="w-4 h-4 text-primary-300"
                  aria-hidden="true"
                />
                <span className="font-open text-xs leading-5 text-primary-300">
                  {time}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* ─── Scale tier hook ──────────────────────────────────────────────── */}
    <section className="pt-25 pb-10 px-5">
      <div className="max-w-315 mx-auto text-center">
        <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3">
          Scale
        </p>
        <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-4">
          Built for research teams that{' '}
          <br className="hidden lg:block" aria-hidden="true" />
          need structured web data
        </h2>
        <p className="text-base leading-6 text-grey-200 mb-10 max-w-180 mx-auto">
          Run discovery, crawling, and prospect capture in one workflow — then
          export structured fields into the tools your analysts already use.
        </p>
        <Link
          to="/use-cases"
          {...getButtonStyles({
            type: 'primary',
            size: 'large',
            additionalClassnames: 'gap-x-2 w-fit m-auto',
          })}
        >
          See what it&apos;s for
          <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
        </Link>
      </div>
    </section>

    {/* ─── FAQs ─────────────────────────────────────────────────────────── */}
    <section className="py-15 lg:py-20 px-5">
      <div className="max-w-315 mx-auto">
        <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
          FAQs
        </h2>
        <FaqList faqs={FAQS} />
      </div>
    </section>

    {/* ─── Bottom CTA ───────────────────────────────────────────────────── */}
    <BottomCTA />
  </div>
);

export const Route = createFileRoute('/_marketing/')({
  component: IndexComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Stark Crawl - Web crawling & structured data for market research',
      description:
        'Discover hosts, crawl pages, extract structured fields, and capture prospects for market research — without building your own scraping stack.',
      keywords:
        'web crawling, structured data extraction, prospect capture, market research crawl, directory scrape, host discovery, Stark Crawl',
      // Still the social card, but no longer preloaded: the hero is now the
      // rendered CoverageDemo, so preloading this would fetch an image the page
      // never shows.
      image: ogImage({ src: '/images/hero.png' }),
      preloadImage: {
        src: '/images/hero.png',
        sizes: '(max-width: 1088px) 100vw, 1048px',
      },
      schema: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${env.webUrl}/#organization`,
            name: 'Stark Crawl',
            url: env.webUrl,
          },
          {
            '@type': 'SoftwareApplication',
            name: 'Stark Crawl',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: env.webUrl,
            description:
              'Web crawling and structured data extraction for market research — discover hosts, crawl pages, and capture prospects.',
            offers: {
              '@type': 'Offer',
              price: String(USAGE_CREDIT_STEPS[0]?.priceGbp ?? 0),
              priceCurrency: 'GBP',
            },
          },
          {
            '@type': 'FAQPage',
            mainEntity: FAQS.map(({ q, a }) => ({
              '@type': 'Question',
              name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
        ],
      },
    },
  }),
  head: basicHead,
});
